import maxBy from 'lodash/maxBy';
import some from 'lodash/some';
import { SRL_COMPONENT_TYPES } from '../../../../constants';
import {
  getComponentsByType,
  getComponentByDescription,
} from '../../../../utils';

const PROMPT_ALIAS = {
  'Future Plans': 'My future plans are...',
};

const PERCENTAGE = SRL_COMPONENT_TYPES.PERCENTAGE;
const MOST_COMMON_MAJOR_DESCRIPTION = '% Most Common Major';

const FIRST_YEAR_DESCRIPTION = '% First Year';
const FIRST_YEAR_ANSWER = '1st year';
const FIRST_YEAR_LABEL = 'are in their first year';

const RETAKING_CLASS_DESCRIPTION = '% Retaking Class';
const RETAKING_CLASS_EXCLUDED_ANSWER =
  'This is my first time taking this class';
const RETAKING_CLASS_LABEL = 'are retaking this class';

const LOWER_THAN_EXPECTED_DESCRIPTION = '% Lower than Expected';
const MUCH_LOWER_THAN_EXPECTED = '1 - Much lower than expected';
const LOWER_THAN_EXPECTED = '2 - Lower than I expected';
const LOWER_THAN_EXPECTED_WITH_NO_TEXT = '2';
const LOWER_THAN_EXPECTED_LABEL = 'received a grade that was';

const COLUMN_CHART = SRL_COMPONENT_TYPES.COLUMN_CHART;
const EXPECTATION_SCALE_DESCRIPTION = 'Expectiation Scale';
const GRADE_RANGE_DESCRIPTION = 'Grade Range';
const GRADE_RANGE_LABEL_MAPPINGS = {
  'Below 60': '< 60',
};
const EXPECTATION_SCALE_LABEL = {
  '1 - Much lower than expected': 'much\nlower',
  2: 'lower',
  '2 - Lower than I expected': 'lower',
  '3 - About what I expected': 'met\nexpectations',
  4: 'higher',
  '4 - Higher than expected': 'higher',
  '5 - Much higher than expected': 'much\nhigher',
};

export const CHART_FULL_HEIGHT = 248;
export const CHART_HEIGHT = 150;

export const getPromptAlias = (prompt = '') => {
  const promptFormatted = prompt.split('-')[0].trim();
  return PROMPT_ALIAS[promptFormatted] || promptFormatted;
};

export const getPercentageByAnswer = (data = [], answer = '') => {
  const item = data.find(
    (component) => component.answer.toLowerCase() === answer.toLowerCase()
  );
  const percent = (item && item.percent) || null;
  return +percent;
};

export const getMostCommonMajorData = (percentagesComponent = {}) => {
  const averageList = getComponentByDescription(
    percentagesComponent,
    MOST_COMMON_MAJOR_DESCRIPTION
  );
  const maxData = maxBy(averageList, (average) => parseFloat(average.percent));
  if (maxData) {
    const { answer, percent } = maxData;
    return { label: `are ${answer} majors`, percent };
  }
  return {};
};

export const getAreInTheirFirstYearData = (percentagesComponent = {}) => {
  const averageList = getComponentByDescription(
    percentagesComponent,
    FIRST_YEAR_DESCRIPTION
  );
  const percent = getPercentageByAnswer(averageList, FIRST_YEAR_ANSWER);
  return percent ||
    (percent === 0 && some(averageList, (item) => +item.percent > 0))
    ? { label: FIRST_YEAR_LABEL, percent }
    : {};
};

export const sumExceptExcludedAnswer = (total = 0, item = {}) => {
  let percent = 0;
  const answer = item.answer || '';
  if (answer.toLowerCase() !== RETAKING_CLASS_EXCLUDED_ANSWER.toLowerCase()) {
    percent = +item.percent;
  }
  return total + percent;
};

export const sumLowerThanExpectedPercentage = (total = 0, item = {}) => {
  let percent = 0;
  const answer = item.answer || '';
  if (
    [
      MUCH_LOWER_THAN_EXPECTED.toLowerCase(),
      LOWER_THAN_EXPECTED.toLowerCase(),
      LOWER_THAN_EXPECTED_WITH_NO_TEXT.toLowerCase(),
    ].includes(answer.toLowerCase())
  ) {
    percent = +item.percent;
  }
  return total + percent;
};

export const getLowerThanExpectedPercentage = (insightsData = []) => {
  const percentagesComponent = getComponentsByType(insightsData, PERCENTAGE);
  const averageList = getComponentByDescription(
    percentagesComponent,
    LOWER_THAN_EXPECTED_DESCRIPTION
  );
  const percent = averageList.reduce(sumLowerThanExpectedPercentage, 0);
  return percent || percent === 0
    ? {
        label: LOWER_THAN_EXPECTED_LABEL,
        percent,
        customLabel: 'lower than expected',
      }
    : {};
};

export const getAreRetakingClassData = (percentagesComponent = {}) => {
  const averageList = getComponentByDescription(
    percentagesComponent,
    RETAKING_CLASS_DESCRIPTION
  );
  const percent = averageList.reduce(sumExceptExcludedAnswer, 0);
  return percent ||
    (percent === 0 && some(averageList, (item) => +item.percent > 0))
    ? { label: RETAKING_CLASS_LABEL, percent }
    : {};
};

export const selectChartDataByDescription = (
  insightsData,
  description = ''
) => {
  const chartComponents = getComponentsByType(insightsData, COLUMN_CHART);
  const chartList = getComponentByDescription(chartComponents, description);
  return chartList;
};

export const getGradeRange = (insightsData) => {
  const gradeRanges = selectChartDataByDescription(
    insightsData,
    GRADE_RANGE_DESCRIPTION
  );
  let below60Answer = null;
  const gradeRangesTransformed = gradeRanges
    .filter(
      ({ answer }) =>
        ![
          'N/A - I did not receive a test score recently',
          'N/A - I did not receive a score recently',
        ].includes(answer)
    )
    .map(({ answer, student_count }) => {
      const label = GRADE_RANGE_LABEL_MAPPINGS[answer] || answer;
      const response = {
        label,
        studentCount: +student_count,
      };
      // Below 60 should be the first item
      // Getting the value to prevent of doing an array.pop() and instert it as a first item
      if (label === GRADE_RANGE_LABEL_MAPPINGS['Below 60']) {
        below60Answer = { ...response };
        return null;
      }
      return { ...response };
    });

  return [below60Answer, ...gradeRangesTransformed].filter(Boolean);
};

export const getExpectationScale = (insightsData) => {
  const data = selectChartDataByDescription(
    insightsData,
    EXPECTATION_SCALE_DESCRIPTION
  );
  const expectationScaleTransformed = data
    .filter(
      ({ answer }) => answer !== 'N/A - I did not receive a  score recently'
    )
    .map(({ answer, student_count }) => {
      const label = EXPECTATION_SCALE_LABEL[answer] || answer;
      return {
        label,
        studentCount: +student_count,
      };
    });
  return [...expectationScaleTransformed];
};

export const selectIntroAveragesData = (insightsData = []) => {
  const percentagesComponent = getComponentsByType(insightsData, PERCENTAGE);
  const majorsData = getMostCommonMajorData(percentagesComponent);
  const firstYearData = getAreInTheirFirstYearData(percentagesComponent);
  const retakingClassData = getAreRetakingClassData(percentagesComponent);
  return [majorsData, firstYearData, retakingClassData];
};
